"use client";

import { AlertCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import * as z from "zod/v4";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { Alert, AlertDescription } from "@workspace/ui/components/alert";
import { Button } from "@workspace/ui/components/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@workspace/ui/components/dialog";
import { Input } from "@workspace/ui/components/input";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@workspace/ui/components/select";
import {
	revalidateLogic,
	useAppForm,
} from "@workspace/ui/components/tanstack-form";

import { deviceManagementApi } from "../api";
import {
	DEVICE_TYPE_LABELS,
	type DeviceType,
	type RegisterDeviceInput,
} from "../types";

interface Location {
	id: string;
	name: string;
}

interface RegisterDeviceDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	tenant: string;
	locations: Location[];
}

const registerDeviceSchema = z.object({
	identifier: z
		.string()
		.min(1, "Device identifier is required")
		.max(255, "Device identifier must not exceed 255 characters"),
	name: z
		.string()
		.min(1, "Device name is required")
		.max(100, "Device name must not exceed 100 characters"),
	type: z.enum(["Till", "Tablet"], {
		message: "Please select a device type",
	}),
	venueId: z.string().min(1, "Please select a venue"),
	venueName: z.string().min(1, "Venue name is required"),
});

type RegisterDeviceFormValues = z.infer<typeof registerDeviceSchema>;

export function RegisterDeviceDialog({
	open,
	onOpenChange,
	tenant,
	locations,
}: RegisterDeviceDialogProps) {
	const queryClient = useQueryClient();

	const mutation = useMutation({
		...deviceManagementApi.registerDeviceOptions(tenant),
		onSuccess: (result) => {
			if (result.success) {
				toast.success("Device registered successfully.");
				queryClient.invalidateQueries({
					queryKey: [tenant, "devices"],
				});
				handleClose(false);
			} else {
				const errorDetails = result.details as { message?: string } | undefined;
				toast.error(
					errorDetails?.message ||
						"Failed to register device. Please try again.",
				);
			}
		},
		onError: () => {
			toast.error("Unable to register device. Please try again.");
		},
	});

	const form = useAppForm({
		defaultValues: {
			identifier: "",
			name: "",
			type: "" as RegisterDeviceFormValues["type"],
			venueId: "",
			venueName: "",
		} as RegisterDeviceFormValues,
		validationLogic: revalidateLogic({
			mode: "submit",
			modeAfterSubmission: "change",
		}),
		validators: {
			onDynamic: registerDeviceSchema,
		},
		onSubmit: async ({ value }) => {
			const input: RegisterDeviceInput = {
				identifier: value.identifier.trim(),
				name: value.name.trim(),
				type: value.type,
				venueId: value.venueId,
				venueName: value.venueName,
			};
			await mutation.mutateAsync(input);
		},
	});

	const handleClose = (o: boolean) => {
		if (!o) {
			form.reset();
			mutation.reset();
		}
		onOpenChange(o);
	};

	const handleVenueChange = (venueId: string) => {
		const venue = locations.find((l) => l.id === venueId);
		form.setFieldValue("venueId", venueId);
		form.setFieldValue("venueName", venue?.name ?? "");
	};

	return (
		<Dialog open={open} onOpenChange={handleClose}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>Register New Device</DialogTitle>
					<DialogDescription>
						Register a device (till or tablet) to your venue. Enter the device
						identifier and choose a friendly name.
					</DialogDescription>
				</DialogHeader>

				<form
					onSubmit={(e) => {
						e.preventDefault();
						e.stopPropagation();
						form.handleSubmit();
					}}
					className="space-y-4"
				>
					{mutation.isError && (
						<Alert variant="destructive">
							<AlertCircle className="h-4 w-4" />
							<AlertDescription>
								Unable to register device. Please try again.
							</AlertDescription>
						</Alert>
					)}

					<form.AppField name="venueId">
						{(field) => (
							<field.FormItem>
								<field.FormLabel>Venue</field.FormLabel>
								<Select
									value={field.state.value}
									onValueChange={handleVenueChange}
								>
									<field.FormControl>
										<SelectTrigger>
											<SelectValue placeholder="Select a venue" />
										</SelectTrigger>
									</field.FormControl>
									<SelectContent>
										{locations.map((location) => (
											<SelectItem key={location.id} value={location.id}>
												{location.name}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
								<field.FormDescription>
									The venue where this device will be used.
								</field.FormDescription>
								<field.FormMessage />
							</field.FormItem>
						)}
					</form.AppField>

					<form.AppField name="type">
						{(field) => (
							<field.FormItem>
								<field.FormLabel>Device Type</field.FormLabel>
								<Select
									value={field.state.value}
									onValueChange={(value) =>
										field.handleChange(
											value as RegisterDeviceFormValues["type"],
										)
									}
								>
									<field.FormControl>
										<SelectTrigger>
											<SelectValue placeholder="Select device type" />
										</SelectTrigger>
									</field.FormControl>
									<SelectContent>
										{(
											Object.entries(DEVICE_TYPE_LABELS) as [
												DeviceType,
												string,
											][]
										)
											// A print proxy device comes from allocating a linked print proxy.
											.filter(([value]) => value !== "PrintProxy")
											.map(([value, label]) => (
												<SelectItem key={value} value={value}>
													{label}
												</SelectItem>
											))}
									</SelectContent>
								</Select>
								<field.FormDescription>
									The type of device you are registering.
								</field.FormDescription>
								<field.FormMessage />
							</field.FormItem>
						)}
					</form.AppField>

					<form.AppField name="identifier">
						{(field) => (
							<field.FormItem>
								<field.FormLabel>Device Identifier</field.FormLabel>
								<field.FormControl>
									<Input
										name={field.name}
										placeholder="Serial number, MAC address, or device token"
										value={field.state.value}
										onChange={(e) => field.handleChange(e.target.value)}
										onBlur={field.handleBlur}
										className="font-mono"
										autoComplete="off"
									/>
								</field.FormControl>
								<field.FormDescription>
									A unique identifier for this device (serial number, MAC
									address, etc.)
								</field.FormDescription>
								<field.FormMessage />
							</field.FormItem>
						)}
					</form.AppField>

					<form.AppField name="name">
						{(field) => (
							<field.FormItem>
								<field.FormLabel>Device Name</field.FormLabel>
								<field.FormControl>
									<Input
										name={field.name}
										placeholder="e.g., Bar Till 1"
										value={field.state.value}
										onChange={(e) => field.handleChange(e.target.value)}
										onBlur={field.handleBlur}
									/>
								</field.FormControl>
								<field.FormDescription>
									A friendly name to identify this device.
								</field.FormDescription>
								<field.FormMessage />
							</field.FormItem>
						)}
					</form.AppField>

					<DialogFooter>
						<form.Subscribe
							selector={(state) => [state.canSubmit, state.isSubmitting]}
						>
							{([canSubmit]) => (
								<>
									<Button
										type="button"
										variant="outline"
										onClick={() => handleClose(false)}
										disabled={mutation.isPending}
									>
										Cancel
									</Button>
									<Button
										type="submit"
										disabled={!canSubmit || mutation.isPending}
									>
										{mutation.isPending && (
											<Loader2 className="mr-2 h-4 w-4 animate-spin" />
										)}
										Register Device
									</Button>
								</>
							)}
						</form.Subscribe>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}
